import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PermissionsProvider } from "@/context/PermissionsContext";
import { CallProvider } from "@/context/CallContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import Layout from "@/components/Layout";
import AIGate from "@/components/AIGate";
import CallOverlay from "@/components/CallOverlay";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import FeedPage from "@/pages/FeedPage";
import ProfilePage from "@/pages/ProfilePage";
import CommunitiesPage from "@/pages/CommunitiesPage";
import CommunityDetailPage from "@/pages/CommunityDetailPage";
import MessagesPage from "@/pages/MessagesPage";
import MarketplacePage from "@/pages/MarketplacePage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import UploadPage from "@/pages/UploadPage";
import DownloadPage from "@/pages/DownloadPage";
import StoragePage from "@/pages/StoragePage";
import ImageAnalysisPage from "@/pages/ImageAnalysisPage";
import VoicePage from "@/pages/VoicePage";
import ActivityPage from "@/pages/ActivityPage";
import AdminPage from "@/pages/AdminPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import CreateProductPage from "@/pages/CreateProductPage";
import CreatorDashboardPage from "@/pages/CreatorDashboardPage";
import VideoCallPage from "@/pages/VideoCallPage";
import AudioCallPage from "@/pages/AudioCallPage";
import AssistantPage from "@/pages/AssistantPage";
import ImageGeneratorPage from "@/pages/ImageGeneratorPage";
import VideoGeneratorPage from "@/pages/VideoGeneratorPage";
import NotificationsPage from "@/pages/NotificationsPage";
import SettingsPage from "@/pages/SettingsPage";
import RulesPage from "@/pages/RulesPage";
import InnerCirclePage from "@/pages/InnerCirclePage";
import OwnerEntryPage from "@/pages/OwnerEntryPage";
import CreatorDesignStudioPage from "@/pages/CreatorDesignStudioPage";
import MyDesignsPage from "@/pages/MyDesignsPage";
import AIProductCreatorPage from "@/pages/AIProductCreatorPage";
import CreatorPage from "@/pages/CreatorPage";
import ProductEditorPage from "@/pages/ProductEditorPage";
import WallpaperDetailPage from "@/pages/WallpaperDetailPage";
import WallpaperCollectionsPage from "@/pages/WallpaperCollectionsPage";
import SocialConnectionsPage from "@/pages/SocialConnectionsPage";
import OAuthCallbackPage from "@/pages/OAuthCallbackPage";
import PublicMarketplacePage from "@/pages/PublicMarketplacePage";
import PublicCreatorsPage from "@/pages/PublicCreatorsPage";
import PublicProductPage from "@/pages/PublicProductPage";
import PricingPage from "@/pages/PricingPage";
import SubscriptionWall from "@/components/SubscriptionWall";
import SubscriptionSuccessPage from "@/pages/SubscriptionSuccessPage";
import DashboardPage from "@/pages/DashboardPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen font-sans text-muted-foreground">Loading...</div>;
  if (!user) return <Navigate to="/auth" replace />;
  // Check subscription: expired users see the subscription wall
  if (user.role !== 'admin' && user.subscription_status === 'expired') {
    return (
      <Layout>
        <SubscriptionWall user={user} />
      </Layout>
    );
  }
  return <Layout>{children}</Layout>;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen font-sans text-muted-foreground">Loading...</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (user.role !== 'admin') return <Navigate to="/feed" replace />;
  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen font-sans text-muted-foreground">Loading...</div>;
  // Redirect authenticated users to feed
  if (user) return <Navigate to="/feed" replace />;
  return children;
}

function SmartMarketplace() {
  const { user } = useAuth();
  if (user) return <Layout><MarketplacePage /></Layout>;
  return <PublicMarketplacePage />;
}

function SmartProduct() {
  const { user } = useAuth();
  if (user) return <Layout><ProductDetailPage /></Layout>;
  return <PublicProductPage />;
}

function SmartProfile() {
  const { user } = useAuth();
  if (user) return <Layout><ProfilePage /></Layout>;
  return <ProfilePage />;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <PermissionsProvider>
        <CurrencyProvider>
        <CallProvider>
        <BrowserRouter>
          <CallOverlay />
          <Routes>
            <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/rules" element={<RulesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            {/* Public Discovery Pages */}
            <Route path="/creators" element={<PublicCreatorsPage />} />
            {/* Smart routes: public view for anonymous, full view for logged-in */}
            <Route path="/marketplace" element={<SmartMarketplace />} />
            <Route path="/products/:productId" element={<SmartProduct />} />
            <Route path="/profile/:userId" element={<SmartProfile />} />
            {/* Protected Routes */}
            <Route path="/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
            <Route path="/communities" element={<ProtectedRoute><CommunitiesPage /></ProtectedRoute>} />
            <Route path="/communities/:communityId" element={<ProtectedRoute><CommunityDetailPage /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
            <Route path="/messages/:partnerId" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
            <Route path="/wallpaper/:productId" element={<ProtectedRoute><WallpaperDetailPage /></ProtectedRoute>} />
            <Route path="/wallpapers" element={<ProtectedRoute><WallpaperCollectionsPage /></ProtectedRoute>} />
            <Route path="/create-product" element={<ProtectedRoute><CreateProductPage /></ProtectedRoute>} />
            <Route path="/creator-dashboard" element={<ProtectedRoute><CreatorDashboardPage /></ProtectedRoute>} />
            <Route path="/upload" element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
            <Route path="/downloads" element={<ProtectedRoute><DownloadPage /></ProtectedRoute>} />
            <Route path="/storage" element={<ProtectedRoute><StoragePage /></ProtectedRoute>} />
            <Route path="/analyze" element={<ProtectedRoute><ImageAnalysisPage /></ProtectedRoute>} />
            <Route path="/voice" element={<ProtectedRoute><AIGate><VoicePage /></AIGate></ProtectedRoute>} />
            <Route path="/video-call" element={<ProtectedRoute><VideoCallPage /></ProtectedRoute>} />
            <Route path="/audio-call" element={<ProtectedRoute><AudioCallPage /></ProtectedRoute>} />
            <Route path="/assistant" element={<ProtectedRoute><AIGate><AssistantPage /></AIGate></ProtectedRoute>} />
            <Route path="/image-generator" element={<ProtectedRoute><AIGate><ImageGeneratorPage /></AIGate></ProtectedRoute>} />
            <Route path="/video-generator" element={<ProtectedRoute><AIGate><VideoGeneratorPage /></AIGate></ProtectedRoute>} />
            <Route path="/credits-success" element={<ProtectedRoute><VideoGeneratorPage /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/inner-circle" element={<ProtectedRoute><InnerCirclePage /></ProtectedRoute>} />
            <Route path="/design-studio" element={<ProtectedRoute><CreatorDesignStudioPage /></ProtectedRoute>} />
            <Route path="/my-designs" element={<ProtectedRoute><MyDesignsPage /></ProtectedRoute>} />
            <Route path="/ai-product-creator" element={<ProtectedRoute><AIGate><AIProductCreatorPage /></AIGate></ProtectedRoute>} />
            <Route path="/create" element={<ProtectedRoute><AIGate><CreatorPage /></AIGate></ProtectedRoute>} />
            <Route path="/products/:productId/edit" element={<ProtectedRoute><ProductEditorPage /></ProtectedRoute>} />
            <Route path="/social-connections" element={<ProtectedRoute><SocialConnectionsPage /></ProtectedRoute>} />
            <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
            <Route path="/activity" element={<ProtectedRoute><ActivityPage /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/owner-entry" element={<OwnerEntryPage />} />
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </CallProvider>
        </CurrencyProvider>
        </PermissionsProvider>
        <Toaster position="bottom-right" />
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
